import { useState, useEffect } from 'react';
import { Search, X, Plus, Clock, MapPin, DollarSign, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { autoScheduleItem, calculateTravelTime } from '../lib/scheduler';
import type { ItineraryItem, ItineraryDay, Location, SchedulingPreferences } from '../types/itinerary';
import toast from 'react-hot-toast';

interface AddActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  day: ItineraryDay;
  existingItems: ItineraryItem[];
  onItemAdded: (item: ItineraryItem) => void;
  tripId: string;
}

interface ActivityResult {
  id: string;
  title: string;
  description?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  price?: number;
  currency?: string;
  duration?: number; // in minutes
  category?: string;
  rating?: number;
  image_url?: string;
}

export default function AddActivityModal({
  isOpen,
  onClose,
  day,
  existingItems,
  onItemAdded,
  tripId,
}: AddActivityModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activities, setActivities] = useState<ActivityResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityResult | null>(null);
  const [adding, setAdding] = useState(false);

  const searchActivities = async () => {
    if (!searchQuery.trim()) {
      // Show empty state for empty search
      setActivities([]);
      return;
    }

    setLoading(true);
    try {
      // Use default coordinates (Paris) since we don't have trip destination coordinates
      // In a real app, you'd pass the trip's destination coordinates
      const defaultLatitude = 48.8566;
      const defaultLongitude = 2.3522;

      const { data, error } = await supabase
        .functions.invoke('search-activities', {
          body: {
            keyword: searchQuery,
            radius: 10000,
            latitude: defaultLatitude,
            longitude: defaultLongitude,
          },
        });

      if (error) throw error;

      // Transform results
      const transformedActivities: ActivityResult[] = (data?.activities || []).map((a: any) => ({
        id: a.id,
        title: a.name || a.title,
        description: a.shortDescription || a.description,
        location: a.address || a.location,
        latitude: a.geoCode?.latitude || a.latitude,
        longitude: a.geoCode?.longitude || a.longitude,
        price: a.price?.amount || a.price || 0,
        currency: a.price?.currencyCode || a.currency || 'USD',
        duration: a.duration ? parseInt(a.duration.replace('PT', '').replace('M', '')) : 120,
        category: a.category || a.type,
        rating: a.rating,
        image_url: a.pictures?.[0] || a.image_url,
      }));

      // Only show real results, not demo data
      setActivities(transformedActivities.length > 0 ? transformedActivities : []);
      
      // If no real results, show message but don't use demo data
      if (transformedActivities.length === 0) {
        toast('No activities found for this search');
      }
    } catch (error) {
      console.error('Error searching activities:', error);
      setActivities([]);
      toast.error('Failed to search activities. Make sure the search API is configured.');
    } finally {
      setLoading(false);
    }
  };

  const getDemoActivities = (): ActivityResult[] => {
    return [
      {
        id: 'demo-1',
        title: 'City Walking Tour',
        description: 'Explore the city\'s main attractions with a knowledgeable guide.',
        location: 'City Center',
        latitude: 48.8566,
        longitude: 2.3522,
        price: 25,
        currency: 'USD',
        duration: 180,
        category: 'tours',
        rating: 4.5,
      },
      {
        id: 'demo-2',
        title: 'Museum Visit',
        description: 'Discover art and history at the famous national museum.',
        location: 'Museum District',
        latitude: 48.8606,
        longitude: 2.3376,
        price: 15,
        currency: 'USD',
        duration: 150,
        category: 'culture',
        rating: 4.8,
      },
      {
        id: 'demo-3',
        title: 'River Cruise',
        description: 'Enjoy a scenic boat ride along the city\'s main river.',
        location: 'River Dock',
        latitude: 48.8634,
        longitude: 2.3521,
        price: 35,
        currency: 'USD',
        duration: 90,
        category: 'activities',
        rating: 4.6,
      },
      {
        id: 'demo-4',
        title: 'Local Food Tour',
        description: 'Taste the best local cuisine and learn about food traditions.',
        location: 'Food Market',
        latitude: 48.8651,
        longitude: 2.3492,
        price: 65,
        currency: 'USD',
        duration: 210,
        category: 'food',
        rating: 4.9,
      },
      {
        id: 'demo-5',
        title: 'Park Picnic',
        description: 'Relax in a beautiful city park with a pre-packed picnic.',
        location: 'Central Park',
        latitude: 48.8584,
        longitude: 2.3420,
        price: 20,
        currency: 'USD',
        duration: 120,
        category: 'outdoor',
        rating: 4.3,
      },
    ];
  };

  useEffect(() => {
    if (isOpen) {
      searchActivities();
    }
  }, [isOpen]);

  const handleAddActivity = async () => {
    if (!selectedActivity) return;

    setAdding(true);
    try {
      // Calculate location
      const location: Location = {
        name: selectedActivity.location || 'Activity Location',
        address: selectedActivity.location || '',
        lat: selectedActivity.latitude || 0,
        lng: selectedActivity.longitude || 0,
      };

      // Create new item with default times (will be auto-scheduled)
      const newItem: Omit<ItineraryItem, 'id'> = {
        item_type: 'activity',
        itemId: selectedActivity.id,
        tripId,
        day_id: day.id,
        dayNumber: day.dayNumber,
        startTime: new Date().toISOString(),
        endTime: new Date().toISOString(),
        duration: selectedActivity.duration || 120,
        location,
        cost: selectedActivity.price || 0,
        currency: selectedActivity.currency || 'USD',
        status: 'planned',
        title: selectedActivity.title,
        description: selectedActivity.description,
      };

      // Auto-schedule based on last item
      const scheduledItem = autoScheduleItem(newItem, existingItems, {
        startTime: 8,
        endTime: 22,
        maxActivitiesPerDay: 6,
        travelTimeBuffer: 30,
        preferredPace: 'moderate',
      });

      // Calculate default start/end times with proper date handling
      // Validate day.date - it must be a valid YYYY-MM-DD format
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      // Validate date format
      const isValidDate = (dateStr: string | undefined): boolean => {
        if (!dateStr) return false;
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(dateStr)) return false;
        const date = new Date(dateStr);
        return !isNaN(date.getTime());
      };
      
      const dayDate = (day.date && isValidDate(day.date)) ? day.date : todayStr;
      const [startHour] = (scheduledItem.startTime || '09:00').split(':');
      const startHourNum = parseInt(startHour) || 9;
      const startTime = `${dayDate}T${String(startHourNum).padStart(2, '0')}:00:00`;
      const endHourNum = startHourNum + Math.floor((scheduledItem.duration || 120) / 60);
      const endTime = `${dayDate}T${String(endHourNum).padStart(2, '0')}:00:00`;

      // Save to Supabase - map to actual database schema columns
      // Schema: itinerary_items has item_type, NOT type; price, NOT cost; TIME for start_time/end_time
      const insertData: any = {
        item_type: 'activity',
        trip_id: tripId,
        day_id: day.id,
        title: scheduledItem.title,
        description: scheduledItem.description,
        location: scheduledItem.location.name || '',
        address: scheduledItem.location.address || '',
        latitude: scheduledItem.location.lat || 0,
        longitude: scheduledItem.location.lng || 0,
        // Extract TIME portion for database (format: "HH:mm")
        start_time: `${String(startHourNum).padStart(2, '0')}:00`,
        end_time: `${String(endHourNum).padStart(2, '0')}:00`,
        price: scheduledItem.cost || 0,
        currency: scheduledItem.currency || 'USD',
      };

      const { data, error } = await supabase
        .from('itinerary_items')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      // Map database response back to ItineraryItem type
      const resultItem: ItineraryItem = {
        id: data.id,
        // Database uses 'item_type', NOT 'type'
        item_type: 'activity',
        type: 'activity',
        itemId: data.item_id || selectedActivity.id,
        tripId: data.trip_id,
        day_id: data.day_id,
        dayNumber: day.dayNumber,
        startTime: `${dayDate}T${data.start_time}:00`,
        endTime: `${dayDate}T${data.end_time}:00`,
        duration: scheduledItem.duration,
        location: {
          name: data.location,
          address: data.address,
          lat: parseFloat(String(data.latitude)) || 0,
          lng: parseFloat(String(data.longitude)) || 0,
        },
        cost: parseFloat(String(data.price)) || 0,
        currency: data.currency || 'USD',
        status: 'planned',
        title: data.title,
        description: data.description,
      };

      toast.success('Activity added to itinerary!');
      onItemAdded(resultItem);
      onClose();
    } catch (error: any) {
      console.error('Error adding activity:', error);
      toast.error(error?.message || 'Failed to add activity');
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">Add Activity</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchActivities()}
              placeholder="Search for activities..."
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              onClick={searchActivities}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Search
            </button>
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No activities found. Try a different search.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activities.map((activity) => (
                <button
                  key={activity.id}
                  onClick={() => setSelectedActivity(activity)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    selectedActivity?.id === activity.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Activity Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {activity.category && (
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full capitalize">
                            {activity.category}
                          </span>
                        )}
                        {activity.rating && (
                          <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1">
                            ⭐ {activity.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900">{activity.title}</h3>
                      {activity.description && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{activity.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        {activity.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {activity.location}
                          </span>
                        )}
                        {activity.duration && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {Math.floor(activity.duration / 60)}h {activity.duration % 60}m
                          </span>
                        )}
                        {activity.price && (
                          <span className="flex items-center gap-1 font-medium text-gray-700">
                            <DollarSign className="h-4 w-4" />
                            {activity.price} {activity.currency}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Selection indicator */}
                    {selectedActivity?.id === activity.id && (
                      <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                        <Plus className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {selectedActivity ? (
              <span className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{selectedActivity.title}</span>
                <span>•</span>
                <span>{selectedActivity.price} {selectedActivity.currency}</span>
              </span>
            ) : (
              <span>Select an activity to add</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddActivity}
              disabled={!selectedActivity || adding}
              className="px-6 py-2 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {adding ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Add Activity
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
